import { useExperiment } from '@/libraries/experiment';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { computed } from 'vue';
import adminConfig from '@/features/promotions/config.json';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { ObjectType } from '@/@types/graphql-types';

export function useBuyboxView({ objectType }: { objectType?: MaybeRefOrGetter<ObjectType | undefined> }) {
	const { country } = useLanguageStore();
	const { activeVariantWithControlGroup: gridSwiperVariant } = useExperiment('EXP_GRID_SWIPER');
	const { activeVariantWithControlGroup: gridRedesignClickVariant } = useExperiment('EXP_GRID_REDESIGN_CLICK');
	const hasActiveGridBuyboxExperiment = computed(() => !!gridSwiperVariant.value || !!gridRedesignClickVariant.value);

	const buyboxConfig = adminConfig?.buybox;

	/* Settings from Config */
	const isGridDefaultFromConfig = computed(
		() =>
			// Default to grid if config value is invalid
			buyboxConfig?.buybox_default !== 'list'
	);

	const countryOverrides = computed(() => {
		const overrideKey = isGridDefaultFromConfig.value ? 'list_buybox_active' : 'grid_buybox_active';

		return buyboxConfig?.[overrideKey] ?? null;
	});

	const pageType = computed(() => {
		if (toValue(objectType) === ObjectType.ShowSeason) return 'season';
		if (toValue(objectType) === ObjectType.Show) return 'show';
		return 'movie';
	});

	/* Title Page Buybox View */
	const isGridBuyboxDefault = computed(() => {
		if (hasActiveGridBuyboxExperiment.value) return true;
		if (!countryOverrides.value || !toValue(objectType)) return isGridDefaultFromConfig.value;

		/* Country Overrides - always returns opposite of default from config */
		const hasActiveCountryOverride =
			// 1. check if there is a country override for current page type
			(countryOverrides.value[pageType.value] ?? []).includes(country.value) ||
			// 2. check if there is a country override for all page types
			(countryOverrides.value.all_titles ?? []).includes(country.value);

		return hasActiveCountryOverride ? !isGridDefaultFromConfig.value : isGridDefaultFromConfig.value;
	});

	return {
		isGridBuyboxDefault,
	};
}
